import { storyService } from '@/features/story/services/story.service'
import { useAppMutation } from '@/shared/hooks/useAppMutation'
import { StoryEntity } from '@/shared/lib/db'
import { AppError } from '@/shared/types/AppError'
import { HookOptions } from '@/shared/types/HookOptions'
import { useQuery, useQueryClient } from '@tanstack/react-query'

export const storyKeys = {
    all: ['stories'] as const,
    list: (projectId: string) => [...storyKeys.all, 'list', projectId] as const,
    detail: (projectId: string, fileName: string) =>
        [...storyKeys.all, 'detail', projectId, fileName] as const,
}

export const useStoriesQuery = (projectId: string | undefined) => {
    return useQuery({
        queryKey: storyKeys.list(projectId!),
        queryFn: () => storyService.getAll(projectId!),
        enabled: !!projectId,
    })
}

export const useStoryQuery = (projectId: string | undefined, fileName: string | undefined) => {
    return useQuery({
        queryKey: storyKeys.detail(projectId!, fileName!),
        queryFn: async () => {
            const data = await storyService.getByTitle(projectId!, fileName!)
            return data ?? null
        },
        enabled: !!projectId && !!fileName,
    })
}

export const useCreateStoryMutation = (options?: HookOptions<void, AppError, StoryEntity>) => {
    const queryClient = useQueryClient()
    return useAppMutation<void, AppError, StoryEntity>({
        mutationFn: (story: StoryEntity) => storyService.save(story),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: storyKeys.list(variables.projectId) })
            options?.onSuccess?.(data, variables)
        },
        onError: options?.onError,
    })
}

export const useUpdateStoryMutation = (options?: HookOptions<void, AppError, StoryEntity>) => {
    const queryClient = useQueryClient()
    return useAppMutation<void, AppError, StoryEntity>({
        mutationFn: (story: StoryEntity) => storyService.save(story),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: storyKeys.list(variables.projectId) })
            options?.onSuccess?.(data, variables)
        },
        onError: options?.onError,
    })
}

export const useDeleteStoryMutation = (
    options?: HookOptions<void, AppError, { projectId: string; fileName: string }>
) => {
    const queryClient = useQueryClient()
    return useAppMutation<void, AppError, { projectId: string; fileName: string }>({
        mutationFn: ({ projectId, fileName }) => storyService.delete(projectId, fileName),
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: storyKeys.list(variables.projectId) })
            options?.onSuccess?.(data, variables)
        },
        onError: options?.onError,
    })
}
